/**
 * AnimatedIllustrations — bespoke, lightweight animated SVG scenes.
 *
 * Design language: dark glass panels, 1.5px strokes, brand accent per page,
 * subtle looping motion (float / flowing dashes / pulses / pings / bar charts).
 * All animation is CSS (opacity/transform/stroke-dashoffset) — GPU-friendly,
 * loops seamlessly, and is fully disabled under prefers-reduced-motion.
 */

// Shared keyframes + helpers. Rendered once by the HeroIllo wrapper.
export function IlloStyles() {
  return (
    <style>{`
      .illo-tb { transform-box: fill-box; transform-origin: center; }
      .illo-float { animation: illoFloat 6s ease-in-out infinite; }
      .illo-float-rev { animation: illoFloat 7.5s ease-in-out infinite reverse; }
      @keyframes illoFloat {
        0%, 100% { transform: translateY(0); }
        50% { transform: translateY(-10px); }
      }
      .illo-dash { animation: illoDash 3s linear infinite; }
      @keyframes illoDash { to { stroke-dashoffset: -120; } }
      .illo-pulse { animation: illoPulse 2.6s ease-in-out infinite; }
      @keyframes illoPulse {
        0%, 100% { opacity: 0.35; }
        50% { opacity: 1; }
      }
      .illo-blink { animation: illoBlink 1.6s steps(2, start) infinite; }
      @keyframes illoBlink { to { opacity: 0.2; } }
      .illo-ping { transform-box: fill-box; transform-origin: center; animation: illoPing 2.4s cubic-bezier(0, 0, 0.2, 1) infinite; }
      @keyframes illoPing {
        0% { transform: scale(1); opacity: 0.6; }
        70%, 100% { transform: scale(2.3); opacity: 0; }
      }
      .illo-type { transform-box: fill-box; transform-origin: 0 50%; animation: illoType 3.2s ease-in-out infinite; }
      @keyframes illoType {
        0% { transform: scaleX(0); }
        55%, 85% { transform: scaleX(1); }
        100% { transform: scaleX(0); }
      }
      .illo-bar { transform-box: fill-box; transform-origin: 50% 100%; animation: illoBar 2.4s ease-in-out infinite; }
      @keyframes illoBar {
        0%, 100% { transform: scaleY(0.45); }
        50% { transform: scaleY(1); }
      }
      .illo-progress { transform-box: fill-box; transform-origin: 0 50%; animation: illoProgress 3.4s ease-in-out infinite; }
      @keyframes illoProgress {
        0% { transform: scaleX(0.05); }
        70%, 88% { transform: scaleX(1); }
        100% { transform: scaleX(0.05); }
      }
      .illo-orbit { transform-box: fill-box; animation: illoOrbit 9s linear infinite; }
      @keyframes illoOrbit { to { transform: rotate(360deg); } }
      .illo-draw { animation: illoDraw 2.8s ease-in-out infinite; }
      @keyframes illoDraw {
        0% { stroke-dashoffset: 48; }
        45%, 80% { stroke-dashoffset: 0; }
        100% { stroke-dashoffset: 48; }
      }
      /* claim document sliding through the shield */
      .illo-doc { transform-box: fill-box; animation: illoDoc 5.2s ease-in-out infinite; }
      @keyframes illoDoc {
        0% { transform: translateX(0); opacity: 0; }
        10% { opacity: 1; }
        45% { transform: translateX(96px); opacity: 1; }
        55% { transform: translateX(112px); opacity: 0; }
        100% { transform: translateX(112px); opacity: 0; }
      }
      /* vehicle driving along the mini-map route */
      .illo-drive { transform-box: fill-box; animation: illoDrive 8s ease-in-out infinite; }
      @keyframes illoDrive {
        0% { transform: translate(0, 0); opacity: 0; }
        8% { opacity: 1; }
        40% { transform: translate(105px, -26px); }
        75% { transform: translate(215px, 2px); opacity: 1; }
        88% { transform: translate(262px, -18px); opacity: 0; }
        100% { transform: translate(262px, -18px); opacity: 0; }
      }
      /* kanban card hopping to the next column */
      .illo-hop { transform-box: fill-box; animation: illoHop 4.5s ease-in-out infinite; }
      @keyframes illoHop {
        0%, 25% { transform: translate(0, 0); }
        45%, 75% { transform: translate(46px, -6px); }
        95%, 100% { transform: translate(0, 0); }
      }
      /* component snapping into a wireframe slot */
      .illo-snap { transform-box: fill-box; animation: illoSnap 3.8s cubic-bezier(0.34, 1.56, 0.64, 1) infinite; }
      @keyframes illoSnap {
        0% { transform: translateY(-16px); opacity: 0; }
        30%, 80% { transform: translateY(0); opacity: 1; }
        100% { transform: translateY(-16px); opacity: 0; }
      }
      /* paper plane flying off */
      .illo-plane { transform-box: fill-box; animation: illoPlane 5s ease-in-out infinite; }
      @keyframes illoPlane {
        0%, 15% { transform: translate(0, 0) rotate(0deg); opacity: 0; }
        25% { opacity: 1; }
        70% { transform: translate(92px, -66px) rotate(10deg); opacity: 1; }
        82%, 100% { transform: translate(120px, -90px) rotate(12deg); opacity: 0; }
      }
      /* typing dots */
      .illo-dot { transform-box: fill-box; animation: illoDot 1.4s ease-in-out infinite; }
      @keyframes illoDot {
        0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
        30% { transform: translateY(-4px); opacity: 1; }
      }
      /* phone screens cross-fading */
      .illo-screen-a { animation: illoScreenA 6s ease-in-out infinite; }
      .illo-screen-b { animation: illoScreenA 6s ease-in-out infinite reverse; }
      @keyframes illoScreenA {
        0%, 42% { opacity: 1; }
        50%, 92% { opacity: 0; }
        100% { opacity: 1; }
      }
      @media (prefers-reduced-motion: reduce) {
        [class^="illo-"], [class*=" illo-"] { animation: none !important; }
      }
    `}</style>
  );
}

// Positioning wrapper — parks an illustration on the right of a hero section,
// behind the copy (no z-index, so the hero's relative content paints above),
// hidden below xl so it never crowds text. The left edge of the artwork fades
// out via CSS mask instead of a dark overlay, so it can't cover the headline.
export function HeroIllo({ children }) {
  const fade = "linear-gradient(to right, transparent 0%, black 42%)";
  return (
    <div
      aria-hidden="true"
      className="hidden xl:flex absolute inset-y-0 right-0 w-[38%] items-center justify-end pr-6 pointer-events-none select-none"
      style={{ WebkitMaskImage: fade, maskImage: fade }}
    >
      <IlloStyles />
      <div className="relative w-[420px] max-w-full opacity-80">{children}</div>
    </div>
  );
}

const glass = { fill: "rgba(255,255,255,0.03)", stroke: "rgba(255,255,255,0.09)" };
const glassDark = { fill: "rgba(5,8,16,0.65)", stroke: "rgba(255,255,255,0.1)" };

/* ── Web Development: browser + live code + responsive phone + pipeline ───── */
export function WebDevIllo() {
  return (
    <svg viewBox="0 0 420 320" fill="none" className="w-full h-auto">
      {/* browser window */}
      <g className="illo-float">
        <rect x="26" y="26" width="252" height="182" rx="16" {...glass} />
        <circle cx="48" cy="48" r="4" fill="#f87171" opacity="0.7" />
        <circle cx="62" cy="48" r="4" fill="#fbbf24" opacity="0.7" />
        <circle cx="76" cy="48" r="4" fill="#34d399" opacity="0.7" />
        <rect x="92" y="41" width="168" height="14" rx="7" fill="rgba(255,255,255,0.05)" />
        {/* code lines */}
        <rect x="46" y="74" width="96" height="8" rx="4" fill="#8b5cf6" opacity="0.65" />
        <rect x="60" y="92" width="150" height="8" rx="4" fill="rgba(255,255,255,0.14)" />
        <rect x="60" y="110" width="118" height="8" rx="4" fill="#22d3ee" opacity="0.55" />
        <rect x="74" y="128" width="132" height="8" rx="4" fill="rgba(255,255,255,0.12)" />
        <rect x="60" y="146" width="88" height="8" rx="4" fill="#34d399" opacity="0.5" />
        {/* actively-typed line + caret */}
        <rect x="46" y="168" width="140" height="8" rx="4" fill="#8b5cf6" opacity="0.8" className="illo-type" />
        <rect x="190" y="165" width="2.5" height="14" fill="#e2e8f0" className="illo-blink" />
      </g>
      {/* responsive phone */}
      <g className="illo-float-rev">
        <rect x="302" y="58" width="88" height="168" rx="16" {...glass} />
        <rect x="316" y="80" width="60" height="10" rx="5" fill="#22d3ee" opacity="0.5" />
        <rect x="316" y="100" width="60" height="34" rx="6" fill="rgba(255,255,255,0.06)" />
        <rect x="316" y="142" width="28" height="26" rx="6" fill="rgba(139,92,246,0.35)" />
        <rect x="348" y="142" width="28" height="26" rx="6" fill="rgba(34,211,238,0.25)" />
        <rect x="316" y="176" width="60" height="8" rx="4" fill="rgba(255,255,255,0.1)" />
        <rect x="330" y="210" width="32" height="4" rx="2" fill="rgba(255,255,255,0.18)" />
      </g>
      {/* sync line browser → phone */}
      <path d="M278 130 H302" stroke="#22d3ee" strokeWidth="1.5" strokeDasharray="5 6" className="illo-dash" opacity="0.6" />
      {/* deploy pipeline */}
      <path d="M56 268 H364" stroke="rgba(255,255,255,0.12)" strokeWidth="1.5" strokeDasharray="5 6" className="illo-dash" />
      {[
        { x: 56, c: "#8b5cf6", d: "0s" },
        { x: 210, c: "#22d3ee", d: "0.5s" },
        { x: 364, c: "#34d399", d: "1s" },
      ].map((n) => (
        <g key={n.x}>
          <circle cx={n.x} cy="268" r="13" fill="#050810" stroke={n.c} strokeWidth="1.5" />
          <circle cx={n.x} cy="268" r="13" fill="none" stroke={n.c} className="illo-ping" style={{ animationDelay: n.d }} />
          <path d={`M${n.x - 4.5} 268 l3 3 l6 -6`} stroke={n.c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="illo-pulse" style={{ animationDelay: n.d }} />
        </g>
      ))}
      <text x="56" y="298" textAnchor="middle" fill="rgba(255,255,255,0.35)" fontSize="9" fontFamily="monospace">build</text>
      <text x="210" y="298" textAnchor="middle" fill="rgba(255,255,255,0.35)" fontSize="9" fontFamily="monospace">test</text>
      <text x="364" y="298" textAnchor="middle" fill="rgba(255,255,255,0.35)" fontSize="9" fontFamily="monospace">deploy</text>
    </svg>
  );
}

/* ── Mobile Apps: phone with cycling screens + notifications ─────────────── */
export function MobileIllo() {
  return (
    <svg viewBox="0 0 420 320" fill="none" className="w-full h-auto">
      {/* back phone */}
      <g transform="rotate(-10 120 160)" opacity="0.5">
        <rect x="72" y="66" width="100" height="196" rx="20" {...glass} />
        <rect x="88" y="92" width="68" height="10" rx="5" fill="rgba(255,255,255,0.1)" />
        <rect x="88" y="112" width="68" height="52" rx="8" fill="rgba(255,255,255,0.05)" />
        <rect x="88" y="174" width="68" height="8" rx="4" fill="rgba(255,255,255,0.08)" />
      </g>
      {/* main phone */}
      <g className="illo-float">
        <rect x="168" y="34" width="128" height="248" rx="24" fill="rgba(5,8,16,0.8)" stroke="rgba(34,211,238,0.35)" strokeWidth="1.5" />
        <rect x="208" y="46" width="48" height="6" rx="3" fill="rgba(255,255,255,0.12)" />
        {/* screen A */}
        <g className="illo-screen-a">
          <rect x="184" y="66" width="96" height="12" rx="6" fill="#22d3ee" opacity="0.5" />
          <rect x="184" y="88" width="96" height="58" rx="10" fill="rgba(34,211,238,0.14)" />
          <rect x="184" y="156" width="44" height="44" rx="10" fill="rgba(255,255,255,0.07)" />
          <rect x="236" y="156" width="44" height="44" rx="10" fill="rgba(139,92,246,0.25)" />
          <rect x="184" y="210" width="96" height="8" rx="4" fill="rgba(255,255,255,0.1)" />
        </g>
        {/* screen B */}
        <g className="illo-screen-b">
          <rect x="184" y="66" width="60" height="12" rx="6" fill="#8b5cf6" opacity="0.6" />
          <rect x="184" y="88" width="96" height="26" rx="8" fill="rgba(255,255,255,0.07)" />
          <rect x="184" y="122" width="96" height="26" rx="8" fill="rgba(255,255,255,0.05)" />
          <rect x="184" y="156" width="96" height="26" rx="8" fill="rgba(34,211,238,0.16)" />
          <rect x="184" y="196" width="96" height="22" rx="11" fill="rgba(139,92,246,0.4)" />
        </g>
        <rect x="212" y="266" width="40" height="4" rx="2" fill="rgba(255,255,255,0.2)" />
      </g>
      {/* notification card */}
      <g className="illo-float-rev">
        <rect x="284" y="86" width="118" height="44" rx="12" {...glassDark} />
        <circle cx="304" cy="108" r="9" fill="rgba(34,211,238,0.25)" stroke="#22d3ee" />
        <rect x="320" y="99" width="70" height="7" rx="3.5" fill="rgba(255,255,255,0.16)" />
        <rect x="320" y="112" width="48" height="6" rx="3" fill="rgba(255,255,255,0.08)" />
        <circle cx="398" cy="90" r="5" fill="#f43f5e" />
        <circle cx="398" cy="90" r="5" fill="none" stroke="#f43f5e" className="illo-ping" />
      </g>
      {/* platform chips */}
      <g className="illo-pulse">
        <rect x="42" y="284" width="64" height="22" rx="11" {...glass} />
        <text x="74" y="299" textAnchor="middle" fill="rgba(255,255,255,0.5)" fontSize="10">iOS</text>
      </g>
      <g className="illo-pulse" style={{ animationDelay: "0.7s" }}>
        <rect x="116" y="284" width="76" height="22" rx="11" {...glass} />
        <text x="154" y="299" textAnchor="middle" fill="rgba(255,255,255,0.5)" fontSize="10">Android</text>
      </g>
      <path d="M106 295 h10 M232 282 v-0" stroke="rgba(255,255,255,0.1)" />
      <path d="M232 34 c0 -14 -60 -10 -120 244" stroke="none" />
    </svg>
  );
}

/* ── AI & Automation: neural network with flowing data + assistant ───────── */
export function AIIllo() {
  const L1 = [70, 130, 190];
  const L2 = [50, 105, 160, 215];
  const L3 = [90, 170];
  return (
    <svg viewBox="0 0 420 320" fill="none" className="w-full h-auto">
      {/* connections */}
      {L1.map((y1) =>
        L2.map((y2) => (
          <path
            key={`a${y1}-${y2}`}
            d={`M78 ${y1} C 140 ${y1}, 150 ${y2}, 205 ${y2}`}
            stroke="rgba(251,191,36,0.18)"
            strokeWidth="1"
            strokeDasharray="4 7"
            className="illo-dash"
            style={{ animationDelay: `${(y1 + y2) % 3}s` }}
          />
        )),
      )}
      {L2.map((y2) =>
        L3.map((y3) => (
          <path
            key={`b${y2}-${y3}`}
            d={`M219 ${y2} C 275 ${y2}, 285 ${y3}, 338 ${y3}`}
            stroke="rgba(34,211,238,0.18)"
            strokeWidth="1"
            strokeDasharray="4 7"
            className="illo-dash"
            style={{ animationDelay: `${(y2 + y3) % 3}s` }}
          />
        )),
      )}
      {/* nodes */}
      {L1.map((y, i) => (
        <circle key={`n1${y}`} cx="71" cy={y} r="7" fill="#050810" stroke="#fbbf24" strokeWidth="1.5" className="illo-pulse" style={{ animationDelay: `${i * 0.4}s` }} />
      ))}
      {L2.map((y, i) => (
        <circle key={`n2${y}`} cx="212" cy={y} r="7" fill="#050810" stroke="#f59e0b" strokeWidth="1.5" className="illo-pulse" style={{ animationDelay: `${0.2 + i * 0.35}s` }} />
      ))}
      {L3.map((y, i) => (
        <g key={`n3${y}`}>
          <circle cx="345" cy={y} r="9" fill="#050810" stroke="#22d3ee" strokeWidth="1.5" />
          <circle cx="345" cy={y} r="9" fill="none" stroke="#22d3ee" className="illo-ping" style={{ animationDelay: `${i * 1.1}s` }} />
        </g>
      ))}
      {/* orbiting sparkle */}
      <g className="illo-orbit" style={{ transformOrigin: "345px 90px" }}>
        <path d="M345 62 l2.2 5.6 5.6 2.2 -5.6 2.2 -2.2 5.6 -2.2 -5.6 -5.6 -2.2 5.6 -2.2z" fill="#fbbf24" opacity="0.8" />
      </g>
      {/* assistant chat bubble */}
      <g className="illo-float">
        <rect x="96" y="238" width="212" height="54" rx="16" {...glassDark} />
        <path d="M120 292 l-8 12 2 -12z" fill="rgba(255,255,255,0.1)" />
        <circle cx="124" cy="265" r="11" fill="rgba(251,191,36,0.18)" stroke="#fbbf24" />
        <path d="M120 265 h8 M124 261 v8" stroke="#fbbf24" strokeWidth="1.4" strokeLinecap="round" />
        <circle cx="158" cy="265" r="3.5" fill="#e2e8f0" className="illo-dot" />
        <circle cx="172" cy="265" r="3.5" fill="#e2e8f0" className="illo-dot" style={{ animationDelay: "0.18s" }} />
        <circle cx="186" cy="265" r="3.5" fill="#e2e8f0" className="illo-dot" style={{ animationDelay: "0.36s" }} />
        <rect x="206" y="258" width="84" height="14" rx="7" fill="rgba(251,191,36,0.14)" className="illo-type" />
      </g>
    </svg>
  );
}

/* ── UI/UX: wireframe artboard, snapping component, palette, cursor ──────── */
export function UIUXIllo() {
  return (
    <svg viewBox="0 0 420 320" fill="none" className="w-full h-auto">
      {/* artboard */}
      <g className="illo-float">
        <rect x="36" y="28" width="256" height="222" rx="16" {...glass} />
        <rect x="56" y="48" width="216" height="26" rx="8" fill="rgba(244,63,94,0.16)" />
        <circle cx="70" cy="61" r="6" fill="rgba(244,63,94,0.45)" />
        <rect x="86" y="56" width="70" height="10" rx="5" fill="rgba(255,255,255,0.14)" />
        {/* column blocks */}
        <rect x="56" y="88" width="126" height="80" rx="10" fill="rgba(255,255,255,0.05)" />
        <path d="M70 148 l22 -22 14 12 20 -18 22 22" stroke="rgba(244,63,94,0.55)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        <rect x="192" y="88" width="80" height="22" rx="6" fill="rgba(255,255,255,0.07)" />
        <rect x="192" y="118" width="80" height="22" rx="6" fill="rgba(255,255,255,0.05)" />
        {/* component snapping into its slot */}
        <rect x="192" y="146" width="80" height="22" rx="6" fill="rgba(244,63,94,0.28)" stroke="rgba(244,63,94,0.5)" className="illo-snap" />
        {/* CTA button that "clicks" */}
        <rect x="56" y="190" width="104" height="30" rx="15" fill="rgba(244,63,94,0.4)" className="illo-pulse" />
        <rect x="72" y="201" width="72" height="8" rx="4" fill="rgba(255,255,255,0.5)" />
        {/* cursor */}
        <g className="illo-hop" style={{ transformOrigin: "150px 214px" }}>
          <path d="M148 206 l14 34 5 -13 13 -5z" fill="#e2e8f0" stroke="#0b0f1a" strokeWidth="1.4" />
        </g>
      </g>
      {/* palette + design tokens */}
      <g className="illo-float-rev">
        <rect x="312" y="52" width="82" height="150" rx="14" {...glassDark} />
        {["#f43f5e", "#8b5cf6", "#22d3ee", "#34d399"].map((c, i) => (
          <g key={c}>
            <circle cx="336" cy={80 + i * 30} r="8" fill={c} opacity="0.85" className="illo-pulse" style={{ animationDelay: `${i * 0.35}s` }} />
            <rect x="352" y={75 + i * 30} width="30" height="9" rx="4.5" fill="rgba(255,255,255,0.1)" />
          </g>
        ))}
      </g>
      {/* connection from palette to artboard */}
      <path d="M312 128 C 300 128, 302 130, 292 130" stroke="rgba(244,63,94,0.4)" strokeWidth="1.4" strokeDasharray="4 6" className="illo-dash" />
      {/* grid dots */}
      <g fill="rgba(255,255,255,0.14)">
        <circle cx="60" cy="284" r="2" /><circle cx="84" cy="284" r="2" /><circle cx="108" cy="284" r="2" />
        <circle cx="132" cy="284" r="2" /><circle cx="156" cy="284" r="2" />
      </g>
    </svg>
  );
}

/* ── Backend / Custom Software: servers, APIs, live dashboard ────────────── */
export function BackendIllo() {
  return (
    <svg viewBox="0 0 420 320" fill="none" className="w-full h-auto">
      {/* server rack */}
      <g className="illo-float">
        {[0, 1, 2].map((i) => (
          <g key={i}>
            <rect x="42" y={40 + i * 52} width="150" height="40" rx="10" {...glass} />
            <circle cx="64" cy={60 + i * 52} r="4" fill="#34d399" className="illo-blink" style={{ animationDelay: `${i * 0.5}s` }} />
            <circle cx="78" cy={60 + i * 52} r="4" fill="rgba(52,211,153,0.35)" className="illo-blink" style={{ animationDelay: `${0.3 + i * 0.4}s` }} />
            <rect x="94" y={55 + i * 52} width="84" height="9" rx="4.5" fill="rgba(255,255,255,0.09)" />
          </g>
        ))}
      </g>
      {/* database */}
      <g className="illo-float-rev">
        <ellipse cx="106" cy="242" rx="42" ry="13" fill="rgba(52,211,153,0.14)" stroke="rgba(52,211,153,0.5)" strokeWidth="1.4" />
        <path d="M64 242 v34 c0 7.2 18.8 13 42 13 s42 -5.8 42 -13 v-34" stroke="rgba(52,211,153,0.5)" strokeWidth="1.4" fill="rgba(52,211,153,0.06)" />
        <ellipse cx="106" cy="259" rx="42" ry="13" stroke="rgba(52,211,153,0.25)" strokeWidth="1" fill="none" />
      </g>
      {/* API flows */}
      <path d="M192 82 C 240 82, 240 96, 262 96" stroke="#34d399" strokeWidth="1.5" strokeDasharray="5 6" className="illo-dash" opacity="0.55" />
      <path d="M192 168 C 236 168, 238 150, 262 148" stroke="#22d3ee" strokeWidth="1.5" strokeDasharray="5 6" className="illo-dash" opacity="0.55" style={{ animationDelay: "0.8s" }} />
      <path d="M106 228 C 106 200, 130 196, 148 196" stroke="rgba(52,211,153,0.4)" strokeWidth="1.4" strokeDasharray="4 6" className="illo-dash" style={{ animationDelay: "1.4s" }} />
      {/* live dashboard */}
      <g className="illo-float" style={{ animationDelay: "1s" }}>
        <rect x="262" y="58" width="136" height="128" rx="14" {...glassDark} />
        <rect x="278" y="74" width="66" height="9" rx="4.5" fill="rgba(255,255,255,0.14)" />
        <circle cx="384" cy="78" r="4" fill="#34d399" className="illo-blink" />
        {[0, 1, 2, 3].map((i) => (
          <rect
            key={i}
            x={282 + i * 28}
            y="104"
            width="16"
            height="64"
            rx="4"
            fill={i === 2 ? "rgba(34,211,238,0.5)" : "rgba(52,211,153,0.4)"}
            className="illo-bar"
            style={{ animationDelay: `${i * 0.3}s` }}
          />
        ))}
      </g>
      {/* request/latency ticker */}
      <g className="illo-pulse">
        <rect x="262" y="210" width="136" height="34" rx="12" {...glass} />
        <circle cx="282" cy="227" r="4" fill="#22d3ee" />
        <text x="296" y="231" fill="rgba(255,255,255,0.45)" fontSize="10" fontFamily="monospace">200 OK · 42ms</text>
      </g>
    </svg>
  );
}

/* ── Cloud: cloud core, containers, deploy progress, orbiting node ───────── */
export function CloudIllo() {
  return (
    <svg viewBox="0 0 420 320" fill="none" className="w-full h-auto">
      {/* cloud */}
      <g className="illo-float">
        <path
          d="M290 118 h-8 a52 52 0 1 0 -102 14 a34 34 0 0 0 6 68 h104 a41 41 0 0 0 0 -82z"
          fill="rgba(56,189,248,0.08)"
          stroke="rgba(56,189,248,0.55)"
          strokeWidth="1.6"
        />
        <circle cx="238" cy="160" r="5" fill="#38bdf8" className="illo-pulse" />
        <circle cx="214" cy="160" r="5" fill="#38bdf8" className="illo-pulse" style={{ animationDelay: "0.4s" }} />
        <circle cx="262" cy="160" r="5" fill="#38bdf8" className="illo-pulse" style={{ animationDelay: "0.8s" }} />
      </g>
      {/* orbiting node */}
      <g className="illo-orbit" style={{ transformOrigin: "238px 150px" }}>
        <circle cx="238" cy="52" r="6" fill="#050810" stroke="#38bdf8" strokeWidth="1.5" />
      </g>
      {/* containers */}
      {[0, 1, 2].map((i) => (
        <g key={i} className={i % 2 ? "illo-float-rev" : "illo-float"} style={{ animationDelay: `${i * 0.5}s` }}>
          <rect x={92 + i * 92} y="236" width="72" height="52" rx="12" {...glass} />
          <rect x={104 + i * 92} y="250" width="48" height="8" rx="4" fill="rgba(56,189,248,0.4)" />
          <rect x={104 + i * 92} y="264" width="34" height="7" rx="3.5" fill="rgba(255,255,255,0.1)" />
        </g>
      ))}
      {/* connections up to cloud */}
      {[128, 220, 312].map((x, i) => (
        <path
          key={x}
          d={`M${x} 236 C ${x} 216, 238 214, 238 200`}
          stroke="rgba(56,189,248,0.35)"
          strokeWidth="1.4"
          strokeDasharray="4 6"
          className="illo-dash"
          style={{ animationDelay: `${i * 0.6}s` }}
        />
      ))}
      {/* deploy progress */}
      <g>
        <rect x="120" y="40" width="180" height="30" rx="12" {...glassDark} />
        <text x="136" y="59" fill="rgba(255,255,255,0.4)" fontSize="10" fontFamily="monospace">deploying…</text>
        <rect x="210" y="52" width="76" height="7" rx="3.5" fill="rgba(255,255,255,0.08)" />
        <rect x="210" y="52" width="76" height="7" rx="3.5" fill="#38bdf8" opacity="0.7" className="illo-progress" />
      </g>
    </svg>
  );
}

/* ── ClaimGuard NEMT: claims flowing through the Denial Shield + dispatch ── */
export function ClaimGuardIllo() {
  return (
    <svg viewBox="0 0 420 340" fill="none" className="w-full h-auto">
      {/* claim pipeline track */}
      <path d="M40 118 H180 M250 118 H380" stroke="rgba(255,255,255,0.1)" strokeWidth="1.4" strokeDasharray="5 6" className="illo-dash" />
      {/* claim documents flowing into the shield */}
      {[0, 2.6].map((delay) => (
        <g key={delay} className="illo-doc" style={{ animationDelay: `${delay}s` }}>
          <rect x="52" y="96" width="34" height="44" rx="6" fill="rgba(5,8,16,0.9)" stroke="rgba(52,211,153,0.55)" strokeWidth="1.3" />
          <rect x="59" y="106" width="20" height="4" rx="2" fill="rgba(255,255,255,0.25)" />
          <rect x="59" y="114" width="14" height="4" rx="2" fill="rgba(255,255,255,0.15)" />
          <rect x="59" y="122" width="18" height="4" rx="2" fill="rgba(52,211,153,0.5)" />
          <text x="69" y="136" textAnchor="middle" fill="rgba(52,211,153,0.8)" fontSize="7" fontFamily="monospace">837P</text>
        </g>
      ))}
      {/* Denial Shield */}
      <g className="illo-float">
        <path d="M215 62 l38 18 v26 c0 24 -17 46 -38 54 c-21 -8 -38 -30 -38 -54 v-26 l38 -18z" fill="rgba(52,211,153,0.1)" stroke="#34d399" strokeWidth="1.8" />
        <path d="M215 62 l38 18 v26 c0 24 -17 46 -38 54 c-21 -8 -38 -30 -38 -54 v-26 l38 -18z" fill="none" stroke="#34d399" className="illo-ping" style={{ transformOrigin: "215px 110px" }} />
        <path d="M200 110 l10 10 l22 -24" stroke="#34d399" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" strokeDasharray="48" className="illo-draw" />
      </g>
      {/* approved chip */}
      <g className="illo-pulse">
        <rect x="278" y="96" width="96" height="30" rx="15" fill="rgba(52,211,153,0.12)" stroke="rgba(52,211,153,0.5)" strokeWidth="1.3" />
        <path d="M292 111 l4 4 l8 -9" stroke="#34d399" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <text x="312" y="115" fill="#34d399" fontSize="10" fontWeight="700">PAID</text>
      </g>
      {/* revenue dashboard */}
      <g className="illo-float-rev">
        <rect x="296" y="150" width="104" height="86" rx="14" {...glassDark} />
        <rect x="310" y="162" width="46" height="8" rx="4" fill="rgba(255,255,255,0.14)" />
        <circle cx="388" cy="166" r="4" fill="#34d399" className="illo-blink" />
        {[0, 1, 2].map((i) => (
          <rect key={i} x={312 + i * 26} y="182" width="14" height="42" rx="4" fill="rgba(52,211,153,0.45)" className="illo-bar" style={{ animationDelay: `${i * 0.35}s` }} />
        ))}
        <text x="312" y="230" fill="rgba(52,211,153,0.7)" fontSize="8" fontFamily="monospace">+recovered $</text>
      </g>
      {/* denied → fixed loop chip */}
      <g className="illo-pulse" style={{ animationDelay: "1s" }}>
        <rect x="40" y="162" width="118" height="30" rx="15" {...glass} />
        <circle cx="58" cy="177" r="4" fill="#fbbf24" />
        <text x="70" y="181" fill="rgba(255,255,255,0.5)" fontSize="9">auth · EVV · codes</text>
      </g>
      {/* dispatch mini-map */}
      <g>
        <rect x="40" y="216" width="230" height="104" rx="16" {...glass} />
        <path d="M56 262 H254 M56 292 H254 M110 224 V312 M180 224 V312" stroke="rgba(255,255,255,0.05)" strokeWidth="1" />
        <path d="M62 300 C 110 262, 150 300, 200 276 S 250 262, 256 258" stroke="url(#cgRouteGrad)" strokeWidth="2.2" strokeLinecap="round" strokeDasharray="8 9" className="illo-dash" />
        <defs>
          <linearGradient id="cgRouteGrad" x1="0" y1="1" x2="1" y2="0">
            <stop offset="0%" stopColor="#34d399" />
            <stop offset="100%" stopColor="#22d3ee" />
          </linearGradient>
        </defs>
        {/* pins */}
        <circle cx="62" cy="300" r="5" fill="#34d399" />
        <circle cx="62" cy="300" r="5" fill="none" stroke="#34d399" className="illo-ping" />
        <path d="M256 240 c-6 0 -11 5 -11 11 c0 8 11 17 11 17 s11 -9 11 -17 c0 -6 -5 -11 -11 -11z" fill="rgba(34,211,238,0.15)" stroke="#22d3ee" strokeWidth="1.5" />
        {/* vehicle driving the route */}
        <g className="illo-drive">
          <circle cx="62" cy="300" r="8" fill="#050810" stroke="#34d399" strokeWidth="1.6" />
          <path d="M58 300 h5 l2 -3 h2 l1.5 3 M58 302 h9" stroke="#34d399" strokeWidth="1.2" strokeLinecap="round" />
        </g>
        <text x="56" y="238" fill="rgba(255,255,255,0.3)" fontSize="8" fontFamily="monospace">live dispatch</text>
      </g>
    </svg>
  );
}

/* ── About: collaborating team, kanban in motion, growth line ────────────── */
export function AboutIllo() {
  return (
    <svg viewBox="0 0 420 320" fill="none" className="w-full h-auto">
      {/* team hub */}
      {[
        { x: 82, y: 66, c: "#8b5cf6", t: "PM" },
        { x: 44, y: 150, c: "#22d3ee", t: "FE" },
        { x: 120, y: 158, c: "#34d399", t: "BE" },
      ].map((a, i) => (
        <g key={a.t} className={i % 2 ? "illo-float-rev" : "illo-float"}>
          <circle cx={a.x} cy={a.y} r="17" fill="rgba(255,255,255,0.04)" stroke={a.c} strokeWidth="1.5" />
          <text x={a.x} y={a.y + 4} textAnchor="middle" fill={a.c} fontSize="10" fontWeight="700">{a.t}</text>
        </g>
      ))}
      <path d="M82 84 L52 134 M96 78 L112 142 M60 152 L104 158" stroke="rgba(139,92,246,0.35)" strokeWidth="1.3" strokeDasharray="4 6" className="illo-dash" />
      {/* idea bulb */}
      <g className="illo-pulse">
        <circle cx="82" cy="26" r="9" fill="rgba(251,191,36,0.15)" stroke="#fbbf24" strokeWidth="1.4" />
        <path d="M82 21 v6 M79 30 h6" stroke="#fbbf24" strokeWidth="1.3" strokeLinecap="round" />
      </g>
      {/* kanban board */}
      <g className="illo-float" style={{ animationDelay: "0.6s" }}>
        <rect x="196" y="38" width="200" height="150" rx="16" {...glass} />
        {[0, 1, 2].map((col) => (
          <rect key={col} x={210 + col * 62} y="54" width="52" height="120" rx="8" fill="rgba(255,255,255,0.03)" />
        ))}
        <rect x="216" y="62" width="40" height="8" rx="4" fill="rgba(255,255,255,0.14)" />
        <rect x="278" y="62" width="40" height="8" rx="4" fill="rgba(255,255,255,0.14)" />
        <rect x="340" y="62" width="40" height="8" rx="4" fill="rgba(255,255,255,0.14)" />
        <rect x="216" y="78" width="40" height="24" rx="6" fill="rgba(139,92,246,0.25)" />
        <rect x="216" y="108" width="40" height="24" rx="6" fill="rgba(255,255,255,0.06)" />
        <rect x="340" y="78" width="40" height="24" rx="6" fill="rgba(52,211,153,0.22)" />
        {/* card moving between columns */}
        <rect x="216" y="138" width="40" height="24" rx="6" fill="rgba(34,211,238,0.3)" stroke="rgba(34,211,238,0.5)" className="illo-hop" />
      </g>
      {/* growth chart */}
      <g className="illo-float-rev" style={{ animationDelay: "0.3s" }}>
        <rect x="196" y="208" width="200" height="96" rx="16" {...glassDark} />
        <path
          d="M214 284 L252 262 L288 270 L322 240 L376 226"
          stroke="#8b5cf6"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeDasharray="180"
          className="illo-draw"
          style={{ animationDuration: "4s" }}
        />
        <circle cx="376" cy="226" r="5" fill="#8b5cf6" />
        <circle cx="376" cy="226" r="5" fill="none" stroke="#8b5cf6" className="illo-ping" />
        <text x="214" y="230" fill="rgba(255,255,255,0.3)" fontSize="9" fontFamily="monospace">team growth</text>
      </g>
      {/* planning doc */}
      <g className="illo-float">
        <rect x="52" y="208" width="112" height="96" rx="14" {...glass} />
        <rect x="68" y="224" width="80" height="8" rx="4" fill="rgba(139,92,246,0.5)" />
        <rect x="68" y="240" width="62" height="7" rx="3.5" fill="rgba(255,255,255,0.1)" />
        <rect x="68" y="254" width="70" height="7" rx="3.5" fill="rgba(255,255,255,0.08)" />
        <path d="M68 276 l5 5 l9 -10" stroke="#34d399" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="illo-pulse" />
        <rect x="88" y="272" width="50" height="7" rx="3.5" fill="rgba(255,255,255,0.1)" />
      </g>
    </svg>
  );
}

/* ── Contact: live chat, paper plane, connection ─────────────────────────── */
export function ContactIllo() {
  return (
    <svg viewBox="0 0 420 320" fill="none" className="w-full h-auto">
      {/* chat window */}
      <g className="illo-float">
        <rect x="56" y="40" width="230" height="226" rx="18" {...glass} />
        <rect x="56" y="40" width="230" height="42" rx="18" fill="rgba(139,92,246,0.12)" />
        <circle cx="82" cy="61" r="9" fill="rgba(139,92,246,0.3)" stroke="#8b5cf6" />
        <rect x="100" y="52" width="76" height="8" rx="4" fill="rgba(255,255,255,0.18)" />
        <rect x="100" y="66" width="44" height="6" rx="3" fill="rgba(52,211,153,0.5)" />
        <circle cx="270" cy="52" r="5" fill="#f43f5e" />
        <circle cx="270" cy="52" r="5" fill="none" stroke="#f43f5e" className="illo-ping" />
        {/* incoming bubbles */}
        <rect x="74" y="100" width="120" height="26" rx="13" fill="rgba(255,255,255,0.06)" className="illo-pulse" />
        <rect x="74" y="134" width="88" height="26" rx="13" fill="rgba(255,255,255,0.05)" className="illo-pulse" style={{ animationDelay: "0.5s" }} />
        {/* outgoing bubbles */}
        <rect x="148" y="168" width="120" height="26" rx="13" fill="rgba(139,92,246,0.3)" className="illo-pulse" style={{ animationDelay: "1s" }} />
        {/* typing indicator */}
        <rect x="74" y="202" width="64" height="26" rx="13" fill="rgba(255,255,255,0.05)" />
        <circle cx="94" cy="215" r="3.2" fill="#e2e8f0" className="illo-dot" />
        <circle cx="106" cy="215" r="3.2" fill="#e2e8f0" className="illo-dot" style={{ animationDelay: "0.18s" }} />
        <circle cx="118" cy="215" r="3.2" fill="#e2e8f0" className="illo-dot" style={{ animationDelay: "0.36s" }} />
        {/* input bar */}
        <rect x="74" y="238" width="164" height="18" rx="9" fill="rgba(255,255,255,0.05)" />
        <circle cx="258" cy="247" r="10" fill="rgba(139,92,246,0.4)" />
        <path d="M254 247 h8 M259 243 l4 4 -4 4" stroke="#fff" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
      </g>
      {/* paper plane + trail */}
      <path d="M286 150 C 320 130, 340 100, 372 74" stroke="rgba(34,211,238,0.35)" strokeWidth="1.3" strokeDasharray="4 7" className="illo-dash" />
      <g className="illo-plane" style={{ transformOrigin: "286px 150px" }}>
        <path d="M282 156 l30 -12 -22 22 -1 -8z" fill="rgba(34,211,238,0.25)" stroke="#22d3ee" strokeWidth="1.4" strokeLinejoin="round" />
      </g>
      {/* response-time chip */}
      <g className="illo-float-rev">
        <rect x="300" y="196" width="104" height="56" rx="14" {...glassDark} />
        <circle cx="320" cy="224" r="8" fill="rgba(52,211,153,0.18)" stroke="#34d399" />
        <path d="M320 219 v5 l3.5 2.5" stroke="#34d399" strokeWidth="1.4" strokeLinecap="round" />
        <rect x="336" y="212" width="56" height="8" rx="4" fill="rgba(255,255,255,0.16)" />
        <rect x="336" y="226" width="40" height="7" rx="3.5" fill="rgba(52,211,153,0.4)" />
      </g>
      {/* video call chip */}
      <g className="illo-float" style={{ animationDelay: "0.8s" }}>
        <rect x="312" y="98" width="92" height="60" rx="14" {...glassDark} />
        <rect x="324" y="110" width="42" height="36" rx="8" fill="rgba(139,92,246,0.25)" />
        <circle cx="345" cy="124" r="7" fill="rgba(255,255,255,0.2)" />
        <path d="M370 120 l14 -8 v32 l-14 -8z" fill="rgba(34,211,238,0.3)" stroke="#22d3ee" strokeWidth="1.2" />
        <circle cx="396" cy="106" r="4" fill="#34d399" className="illo-blink" />
      </g>
    </svg>
  );
}
